import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './MyLearning.css';
import { FaStar, FaPlayCircle, FaCircle, FaSearch } from 'react-icons/fa';
import axios from 'axios';
import UsersTopbar from '../UsersTopbar/UsersTopbar';
import api from '../../api';
import useBlockCheck from '../../hooks/useBlockCheck';
import { useAuth } from '../../context/AuthContext';
import { Button } from 'antd';
import { useState as useGiftState } from 'react';

const MyLearning = () => {
  useBlockCheck('user');
  const { token } = useAuth();
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(''); // State for search query
  const [giftNotifications, setGiftNotifications] = useGiftState([]);
  const navigate = useNavigate();

  const fetchCourseProgress = async (courseId) => {
    try {
      // console.log("Fetching progress for course:", courseId);
      const response = await axios.get(api.getCourseProgress(courseId), {
        headers: { Authorization: `Bearer ${token}` },
      });

      // console.log("Raw progress data from API:", response.data);

      // Extract and validate lastPlayedLesson
      const lastPlayed = response.data.lastPlayedLesson;
      if (lastPlayed) {
        // console.log("Last played lesson found:", {
        //     lessonId: lastPlayed.lessonId,
        //     lastPosition: lastPlayed.lastPosition
        // });
      } else {
        // console.log("No last played lesson data found in API response");
      }

      return response.data;
    } catch (error) {
      console.error(`Error fetching progress for course ${courseId}:`, error);
      return { watchedVideos: {}, videoProgress: {} };
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchPurchasedCourses = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(api.getPurchasedCourses, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const coursesWithProgress = await Promise.all(
          response.data.courses.map(async (course) => {
            const progress = await fetchCourseProgress(course._id);
            return { ...course, progress };
          }),
        );

        setCourses(coursesWithProgress);
      } catch (error) {
        console.error('Error fetching purchased courses:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPurchasedCourses();
  }, [token, navigate]);

  useEffect(() => {
    // Fetch notifications for received gifts
    const fetchGiftNotifications = async () => {
      try {
        const res = await axios.get(api.getProfile, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setGiftNotifications(
          (res.data.user.notifications || []).filter(n => n.message.includes('received the course')),
        );
      } catch (err) {
        setGiftNotifications([]);
      }
    };
    fetchGiftNotifications();
  }, [token]);

  // Filter courses based on search query
  const filteredCourses = searchQuery.trim() === ''
    ? courses
    : courses.filter(course =>
      course.title.toLowerCase().includes(searchQuery.toLowerCase()),
    );

  // Progress circle component
  const ProgressCircleIcon = ({ progress }) => {
    return (
      <div className="my-learning-progress-circle-container">
        <FaCircle
          size={40}
          className="my-learning-progress-circle-icon"
        />
        <div className="my-learning-progress-circle-text">
          {progress}%
        </div>
      </div>
    );
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Improved progress calculation with better handling of lastPlayedLesson flag
  const getCourseProgress = (course) => {
    if (!course.progress) {
      // console.log(`Course ${course.title} has no progress data`);
      return { progress: 0, lastPlayedVideoId: null, lastPosition: 0, videoUrl: null };
    }

    // console.log(`Course progress data for ${course.title}:`, course.progress);

    // Look for the lastPlayedLesson property which will now be set by the backend
    const progressData = course.progress;
    let lastPlayedData = null;

    // Check if lastPlayedLesson exists in the response (should be set by our backend now)
    if (progressData.lastPlayedLesson) {
      lastPlayedData = progressData.lastPlayedLesson;
      // console.log("Found lastPlayedLesson property:", lastPlayedData);
    }
    // Fallback: if for some reason lastPlayedLesson isn't set, look for any lesson marked with lastPlayedLesson=true
    else if (progressData.videoProgress) {
      const videoProgress = progressData.videoProgress;
      for (const lessonId in videoProgress) {
        const lesson = videoProgress[lessonId];
        if (lesson.lastPlayedLesson) {
          lastPlayedData = {
            lessonId: lessonId,
            lastPosition: lesson.lastPosition || 0,
            watchedDuration: lesson.watchedDuration || 0,
          };
          // console.log("Found lesson with lastPlayedLesson=true flag:", lastPlayedData);
          break;
        }
      }

      // If still no last played lesson found, fallback to most recently watched
      if (!lastPlayedData) {
        // Find the most recently updated lesson
        const videoIds = Object.keys(videoProgress);
        if (videoIds.length > 0) {
          // Sort by most watched duration or lastPosition as a fallback
          const sortedIds = videoIds.sort((a, b) => {
            // Prioritize lessons that are marked as watched
            if (videoProgress[a].watched && !videoProgress[b].watched) return -1;
            if (!videoProgress[a].watched && videoProgress[b].watched) return 1;

            const watchedA = videoProgress[a].watchedDuration || 0;
            const watchedB = videoProgress[b].watchedDuration || 0;

            // Sort by watched duration (more watched first)
            return watchedB - watchedA;
          });

          const mostWatchedId = sortedIds[0];
          lastPlayedData = {
            lessonId: mostWatchedId,
            lastPosition: videoProgress[mostWatchedId].lastPosition || 0,
            watchedDuration: videoProgress[mostWatchedId].watchedDuration || 0,
          };
          // console.log("Inferred last played data from most watched lesson:", lastPlayedData);
        }
      }
    }

    // Calculate overall progress percentage
    const videoProgress = progressData.videoProgress || {};
    let watchedSeconds = 0;

    Object.keys(videoProgress).forEach((lessonId) => {
      watchedSeconds += videoProgress[lessonId]?.watchedDuration || 0;
    });

    const totalSeconds = course.totalHours ? course.totalHours * 3600 : 0;
    const progressPercent = totalSeconds > 0
      ? Math.min(100, Math.round((watchedSeconds / totalSeconds) * 100))
      : 0;

    return {
      progress: progressPercent,
      lastPlayedVideoId: lastPlayedData?.lessonId || null,
      lastPosition: lastPlayedData?.lastPosition || 0,
      videoUrl: lastPlayedData?.videoUrl || null,
    };
  };

  // Update the handleViewCourse function
  const handleViewCourse = (courseId, lastPlayedVideoId = null, lastPosition = 0, videoUrl = null) => {
    // Don't pass null values, only pass the data when it's actually available
    const navigationState = {};

    if (lastPlayedVideoId) {
      navigationState.lastPlayedVideoId = lastPlayedVideoId;
      if (lastPosition > 0) {
        navigationState.lastPosition = lastPosition;
      }
      if (videoUrl) {
        navigationState.videoUrl = videoUrl;
      }
    }

    // console.log("Navigating to course with data:", { courseId, ...navigationState });

    navigate(`/course/${courseId}`, {
      state: navigationState,
    });
  };

  const handleReceiveGift = async (courseId) => {
    try {
      await axios.post(api.receiveGift, { courseId }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.reload();
    } catch (err) {
      alert('Error receiving gift.');
    }
  };

  return (
    <div className="my-learning-wrapper">
      {/* Top navigation bar with search */}
      <div className="my-learning-nav">
        <UsersTopbar />
      </div>

      {/* Search bar in fixed position like wishlist */}
      {courses.length > 0 && (
        <div className="my-learning-search-wrapper">
          <div className="my-learning-search-container">
            <FaSearch className="my-learning-search-icon" />
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search courses in My Learning..."
              className="my-learning-search-input"
            />
          </div>
        </div>
      )}

      <div className="my-learning-container">
        <div className="my-learning-header">
          <h2 className="my-learning-title">My Learning</h2>
          {/* <p className="my-learning-subtitle">Continue your learning journey</p> */}
        </div>

        {isLoading ? (
          <div className="my-learning-loading">
            <div className="my-learning-spinner"></div>
            <p>Loading your courses...</p>
          </div>
        ) : courses.length === 0 ? (
          <div className="my-learning-empty">
            <div className="my-learning-empty-icon">
              <FaPlayCircle />
            </div>
            <h3 className="my-learning-empty-title">No courses yet</h3>
            <p className="my-learning-empty-text">Your purchased courses will appear here</p>
            <button
              className="my-learning-empty-button"
              onClick={() => navigate('/category/all-courses/all-courses?search=')}
            >
                            Browse Courses
            </button>
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="my-learning-empty">
            <div className="my-learning-empty-icon">
              <FaSearch />
            </div>
            <h3 className="my-learning-empty-title">No matches found</h3>
            <p className="my-learning-empty-text">Try different search terms</p>
            <button
              className="my-learning-empty-button"
              onClick={() => setSearchQuery('')}
            >
                            Clear Search
            </button>
          </div>
        ) : (
          <div className="my-learning-grid">
            {filteredCourses.map((course) => {
              const progressData = getCourseProgress(course);
              const progress = progressData.progress;
              const lastPlayedVideoId = progressData.lastPlayedVideoId;
              const lastPosition = progressData.lastPosition;
              const videoUrl = progressData.videoUrl;

              {/* console.log(`Course ${course.title} progress:`, progressData); */}

              return (
                <div className="my-learning-card" key={course._id}>
                  <div className="my-learning-card-image-container">
                    {course.image && (
                      <img
                        src={`${api.img + course.image}`}
                        alt={course.title}
                        className="my-learning-card-image"
                      />
                    )}
                    <div className="my-learning-card-overlay">
                      <button
                        className="my-learning-card-play-button"
                        onClick={() => handleViewCourse(course._id, lastPlayedVideoId, lastPosition, videoUrl)}
                      >
                        <FaPlayCircle />
                      </button>
                    </div>
                    <div className="my-learning-progress-bar">
                      <div
                        className="my-learning-progress-fill"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="my-learning-card-content">
                    <div className="my-learning-card-header">
                      <h3 className="my-learning-card-title">{course.title}</h3>
                      <ProgressCircleIcon progress={progress} />
                    </div>
                    <div className="my-learning-card-meta">
                      <div className="my-learning-instructor">{course.instructor ? `By ${course.instructor}` : ''}</div>
                      <div className="my-learning-card-rating">
                        {Array.from({ length: 5 }).map((_, index) => (
                          <FaStar
                            key={index}
                            className={
                              index < Math.round(course.averageRating || 0)
                                ? 'my-learning-star-filled'
                                : 'my-learning-star'
                            }
                          />
                        ))}
                        <span className="my-learning-rating-count">({course.totalRatings || 0})</span>
                      </div>
                    </div>
                    <button
                      className="my-learning-card-button"
                      onClick={() => handleViewCourse(course._id, lastPlayedVideoId, lastPosition, videoUrl)}
                    >
                      {lastPlayedVideoId ?
                        `Continue (${progress}% complete)` :
                        'Start Course'
                      }
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Gift Notifications */}
        {giftNotifications.length > 0 && (
          <div className="my-learning-gift-notifications">
            <h3>🎁 Gifts Received</h3>
            {giftNotifications.map((n, idx) => (
              <div key={idx} className="my-learning-gift-notification">
                <span>{n.message}</span>
                {!n.read && (
                  <Button type="primary" onClick={() => handleReceiveGift(n.courseId)}>
                                        Receive Gift
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyLearning;